import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';
export type ToastType = 'success' | 'error' | 'info';
interface Toast {
  id: string;
  message: string;
  type: ToastType;
}
interface ToastContextType {
  showToast: (message: string, type?: ToastType) => void;
}
const ToastContext = createContext<ToastContextType | undefined>(undefined);
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
export function ToastProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts(prev => [...prev, {
      id,
      message,
      type
    }]);
    // Auto dismiss
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  }, []);
  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);
  return <ToastContext.Provider value={{
    showToast
  }} data-id="element-1137">
      {children}
      <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none" data-id="element-1138">
        {toasts.map(toast => <div key={toast.id} className="pointer-events-auto flex items-center gap-3 bg-white pl-4 pr-2 py-3 rounded-xl shadow-lg border border-[#e5e5e5] min-w-[300px] animate-in slide-in-from-right-full fade-in duration-300 relative overflow-hidden" data-id="element-1139">
            {/* Colored Accent Bar */}
            <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${toast.type === 'success' ? 'bg-emerald-500' : toast.type === 'error' ? 'bg-rose-500' : 'bg-sky-500'}`} data-id="element-1140" />

            {/* Icon */}
            <div className="flex-shrink-0" data-id="element-1141">
              {toast.type === 'success' && <CheckCircle className="h-5 w-5 text-emerald-500" data-id="element-1142" />}
              {toast.type === 'error' && <AlertCircle className="h-5 w-5 text-rose-500" data-id="element-1143" />}
              {toast.type === 'info' && <Info className="h-5 w-5 text-sky-500" data-id="element-1144" />}
            </div>

            {/* Message */}
            <p className="text-sm font-medium text-[#062133] flex-1" data-id="element-1145">
              {toast.message}
            </p>

            {/* Close Button */}
            <button onClick={() => removeToast(toast.id)} className="p-1.5 text-[#5a7280] hover:text-[#062133] hover:bg-[#fafaf9] rounded-lg transition-colors" data-id="element-1146">
              <X className="h-4 w-4" data-id="element-1147" />
            </button>
          </div>)}
      </div>
    </ToastContext.Provider>;
}